/**
 * Map Location Data
 *
 * This file contains all location data for the interactive map on the Contact page.
 *
 * CMS INTEGRATION:
 * Replace the static arrays below with API calls to your CMS.
 * Example:
 *   export async function getClientLocations(): Promise<MapLocation[]> {
 *     const res = await fetch('/api/cms/client-locations');
 *     return res.json();
 *   }
 *
 * COORDINATE SYSTEM:
 * - x: percentage from left edge of the map (0 = far left, 100 = far right)
 * - y: percentage from top edge of the map (0 = top, 100 = bottom)
 *
 * The map SVG uses a dot-grid world map projection.
 * To find coordinates for a new location:
 * 1. Open the map SVG in a browser
 * 2. Hover over the target area
 * 3. Calculate x% = (mouseX / mapWidth) * 100
 * 4. Calculate y% = (mouseY / mapHeight) * 100
 */

export interface MapLocation {
  /** Unique identifier - used as React key and for CMS reference */
  id: string
  /** Display name shown on hover */
  name: string
  /** Horizontal position as percentage (0-100) from left */
  x: number
  /** Vertical position as percentage (0-100) from top */
  y: number
  /** Optional: additional details for expanded view */
  details?: string
}

/**
 * Client locations - countries/regions where CSC Group serves clients
 */
export const clientLocations: MapLocation[] = [
  // Middle East
  { id: 'lebanon', name: 'Lebanon', x: 55.5, y: 39 },
  { id: 'iraq', name: 'Iraq', x: 58, y: 39.5 },
  { id: 'jordan', name: 'Jordan', x: 55, y: 40.5 },
  { id: 'kuwait', name: 'Kuwait', x: 59, y: 41 },
  { id: 'saudi', name: 'Saudi Arabia', x: 57, y: 44 },
  { id: 'uae', name: 'UAE', x: 60, y: 44 },
  { id: 'bahrain', name: 'Bahrain', x: 59.5, y: 42.5 },
  { id: 'qatar', name: 'Qatar', x: 59.5, y: 43 },
  { id: 'oman', name: 'Oman', x: 61, y: 44.5 },
  { id: 'yemen', name: 'Yemen', x: 58, y: 46 },
  { id: 'palestine', name: 'Palestine', x: 55, y: 40 },
  { id: 'syria', name: 'Syria', x: 56, y: 38 },

  // Europe
  { id: 'cyprus', name: 'Cyprus', x: 54, y: 37.5 },
  { id: 'greece', name: 'Greece', x: 52, y: 37 },
  { id: 'uk', name: 'United Kingdom', x: 48, y: 30 },
  { id: 'germany', name: 'Germany', x: 50.5, y: 31 },
  { id: 'france', name: 'France', x: 49, y: 33 },
  { id: 'italy', name: 'Italy', x: 50.5, y: 35 },
  { id: 'turkey', name: 'Turkey', x: 54, y: 36 },

  // Africa
  { id: 'egypt', name: 'Egypt', x: 53, y: 42 },
  { id: 'libya', name: 'Libya', x: 50.5, y: 41 },
  { id: 'tunisia', name: 'Tunisia', x: 49.5, y: 38 },
  { id: 'sudan', name: 'Sudan', x: 54, y: 47 },
  { id: 'ethiopia', name: 'Ethiopia', x: 56, y: 51 },
  { id: 'kenya', name: 'Kenya', x: 56, y: 55 },
  { id: 'nigeria', name: 'Nigeria', x: 48, y: 51 },
  { id: 'south-africa', name: 'South Africa', x: 53, y: 66 },
  { id: 'tanzania', name: 'Tanzania', x: 56, y: 57 },

  // Asia
  { id: 'pakistan', name: 'Pakistan', x: 64, y: 40 },
  { id: 'india', name: 'India', x: 67, y: 44 },

  // More Demo Clients
  { id: 'morocco', name: 'Morocco', x: 47, y: 37 },
  { id: 'algeria', name: 'Algeria', x: 49, y: 38 },
  { id: 'ghana', name: 'Ghana', x: 47, y: 51 },
  { id: 'ivory-coast', name: 'Ivory Coast', x: 46, y: 51 },
  { id: 'cameroon', name: 'Cameroon', x: 50, y: 53 },
  { id: 'uganda', name: 'Uganda', x: 55, y: 55 },
  { id: 'zambia', name: 'Zambia', x: 54, y: 62 },
  { id: 'zimbabwe', name: 'Zimbabwe', x: 55, y: 64 },
  { id: 'botswana', name: 'Botswana', x: 54, y: 65 },
  { id: 'namibia', name: 'Namibia', x: 52, y: 64 },
]

/**
 * Office locations - CSC Group physical offices and data centers
 */
export const officeLocations: MapLocation[] = [
  {
    id: 'cyprus-office',
    name: 'Cyprus',
    x: 54,
    y: 37.5,
    details: '3 Zachariadhes Court, 15 Nicodemou Mylona Street, Larnaca 6010',
  },
  {
    id: 'lebanon-office',
    name: 'Lebanon',
    x: 55.5,
    y: 39,
    details: '150 Commodore Street, Hamra, Beirut 1103 2120',
  },
  {
    id: 'iraq-office',
    name: 'Iraq',
    x: 58,
    y: 39.5,
    details: 'Karada Dakhel - Dis.901, St.3, Bldg 4, Baghdad',
  },
  {
    id: 'greece-datacenter',
    name: 'Athens, Greece',
    x: 52,
    y: 37,
    details: 'Digital Reality Colocation Site, Athens',
  },
]
